import { LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSiteBranding } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { trackLeadIntent } from "@/lib/leadIntent";

const initialForm = { name: "", phone: "", email: "" };

export const InvestmentForecastReportModal = ({ listing, onClose, onUnlocked, open }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const { requestParams } = useSiteBranding();

  useEffect(() => {
    if (!open) setForm(initialForm);
  }, [open]);

  if (!open || !listing) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const previewTab = window.open("", "_blank");
    if (previewTab) {
      previewTab.document.write(`
        <html>
          <head><title>Preparing your Signature Memo…</title></head>
          <body style="margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#2C2C2C;color:#F9F8F6;font-family:Inter,system-ui,sans-serif;">
            <div style="text-align:center;padding:24px;max-width:420px;">
              <div style="font-size:12px;letter-spacing:0.24em;text-transform:uppercase;color:#D4AF37;font-weight:700;">Signature Forecast</div>
              <h1 style="margin:16px 0 10px;font-size:32px;line-height:1.1;font-family:'Playfair Display','Cabinet Grotesk',serif;">Preparing your memo…</h1>
              <p style="margin:0;font-size:15px;line-height:1.8;color:rgba(249,248,246,0.82);">Your Alpha Forecast Memo will open here in a moment.</p>
            </div>
          </body>
        </html>
      `);
      previewTab.document.close();
    }
    setLoading(true);
    try {
      const response = await api.post(`/listings/${listing.id}/investment-forecast-report`, form, { params: requestParams, responseType: "blob" });
      window.localStorage.setItem("signature-advisor-captured-lead", JSON.stringify(form));
      const blob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      if (previewTab && !previewTab.closed) {
        previewTab.location.replace(downloadUrl);
      } else {
        window.open(downloadUrl, "_blank");
      }
      window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 60_000);
      await trackLeadIntent({
        premiumLead: form,
        eventType: "pdf_download",
        listing,
        metadata: { pdf_type: "investment_forecast", district: listing.district },
      });
      toast.success("Your Alpha Forecast Memo is open in a new tab");
      onUnlocked?.(form);
      onClose();
    } catch (error) {
      if (previewTab && !previewTab.closed) previewTab.close();
      toast.error(error.response?.data?.detail || "Unable to unlock the investment report right now");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 px-4 py-8 backdrop-blur-sm" data-testid="investment-forecast-modal-overlay">
      <div className="signature-forecast-modal-panel w-full max-w-xl rounded-[30px] p-6 text-white md:p-8" data-testid="investment-forecast-modal-panel">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">Lead-Gated Signature Memo</p>
            <h3 className="mt-2 text-3xl font-semibold text-white" data-testid="investment-forecast-modal-title">
              Get Your Signature Investment Forecast
            </h3>
            <p className="mt-3 text-sm leading-7 text-white/78" data-testid="investment-forecast-modal-description">
              Complete the lead form to unlock the branded Alpha Forecast Memo for {listing.title}. Once submitted, the memo opens in a new tab for a premium review experience.
            </p>
          </div>
          <button className="signature-forecast-modal-close rounded-full p-3 text-white" data-testid="investment-forecast-modal-close-button" onClick={onClose} type="button">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-6 grid gap-3 rounded-[24px] border border-[#D4AF37]/18 bg-white/[0.03] p-4 text-sm text-white/72" data-testid="investment-forecast-modal-benefits">
          <p>• Unlocks immediately after submission</p>
          <p>• Opens in a new tab for desktop and mobile continuity</p>
          <p>• Includes the branded Signature chart and forecast summary</p>
        </div>

        <form className="grid gap-4" onSubmit={handleSubmit}>
          <input autoComplete="name" className="signature-forecast-modal-input" data-testid="investment-forecast-name-input" onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Full Name" required value={form.name} />
          <input autoComplete="tel" className="signature-forecast-modal-input" data-testid="investment-forecast-phone-input" onChange={(event) => setForm({ ...form, phone: event.target.value })} placeholder="Phone Number" required value={form.phone} />
          <input autoComplete="email" className="signature-forecast-modal-input" data-testid="investment-forecast-email-input" onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder="Email Address" required type="email" value={form.email} />
          <button className="gold-button w-full !py-4 !text-sm !tracking-[0.18em]" data-testid="investment-forecast-submit-button" disabled={loading} type="submit">
            {loading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : null}
            Open Alpha Forecast Memo
          </button>
        </form>
      </div>
    </div>
  );
};